import React, { useState } from 'react';
import { connect } from 'react-redux';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import TuneIcon from '@mui/icons-material/Tune';
import agent from '../../agent';
import useTranslation from '../../customHooks/translations';
import { CONTEXT_CHANGE, VISIBLE_CONTEXTS_CHANGE } from '../../constants/actionTypes';
import { DEFAULT_THEME_COLOR } from '../../constants/theme';
import { getFreakIcon } from '../../utils/freakUtils';
import { capitalizeFirstLetter } from '../../utils/commonUtils';
import { ALL_CONTEXTS, getVisibleContexts } from '../../utils/contextUtils';

const tileSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  minHeight: 48,
  px: 1.5,
  borderRadius: 2,
  color: 'white',
  backgroundColor: DEFAULT_THEME_COLOR,
  justifyContent: 'flex-start',
  textAlign: 'left',
};

/**
 * Context switcher showing only the contexts the user chose, with a "Customize" mode
 * to pick which ones are shown (saved on the user, shared with mobile).
 * `compact` renders icon-only tiles (top drawer); otherwise tiles have labels.
 */
const ContextPicker = ({ currentUser, context, compact, onSelected, onContextChange, onVisibleContextsChange }) => {
  const translation = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const label = (c) => translation.settings[`context${capitalizeFirstLetter(c)}`] || c;
  const visible = getVisibleContexts(currentUser, context);

  const select = (c) => {
    onContextChange(c);
    onSelected?.(c);
  };

  const startEditing = () => {
    const chosen = currentUser?.visibleContexts || [];
    setDraft(chosen.length ? chosen : ALL_CONTEXTS);
    setError('');
    setEditing(true);
  };

  const toggle = (c) =>
    setDraft(current => (current.includes(c) ? current.filter(x => x !== c) : [...current, c]));

  const save = async (next) => {
    setSaving(true);
    setError('');
    try {
      // storing "all" as empty keeps newly added contexts visible by default
      const value = next.length === ALL_CONTEXTS.length ? [] : ALL_CONTEXTS.filter(c => next.includes(c));
      await agent.Auth.save({ visibleContexts: value });
      onVisibleContextsChange(value);
      setEditing(false);
    } catch {
      setError('Error');
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
          {translation.contextPicker.customizeTitle}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          {translation.contextPicker.customizeHint}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 2 }}>
          {ALL_CONTEXTS.map(c => (
            <ButtonBase
              key={c}
              onClick={() => toggle(c)}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 48, px: 1, borderRadius: 2, justifyContent: 'flex-start' }}
            >
              <Box sx={{ color: DEFAULT_THEME_COLOR, display: 'flex' }}>{getFreakIcon(c)}</Box>
              <Typography sx={{ flex: 1, textAlign: 'left' }}>{label(c)}</Typography>
              <Switch
                checked={draft.includes(c)}
                onClick={(e) => e.stopPropagation()}
                onChange={() => toggle(c)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: DEFAULT_THEME_COLOR },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: DEFAULT_THEME_COLOR },
                }}
              />
            </ButtonBase>
          ))}
        </Box>
        {(error || !draft.length) && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {error || translation.contextPicker.selectAtLeastOne}
          </Typography>
        )}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
          <Button
            variant="contained"
            disabled={saving || !draft.length}
            onClick={() => save(draft)}
            sx={{ minHeight: 44, backgroundColor: DEFAULT_THEME_COLOR, textTransform: 'none' }}
          >
            {translation.contextPicker.save}
          </Button>
          <Button disabled={saving} onClick={() => setDraft(ALL_CONTEXTS)} sx={{ minHeight: 44, textTransform: 'none' }}>
            {translation.contextPicker.showAll}
          </Button>
          <Button disabled={saving} onClick={() => setEditing(false)} sx={{ minHeight: 44, textTransform: 'none' }}>
            {translation.contextPicker.cancel}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5 }}>
      {visible.map(c => (
        <ButtonBase
          key={c}
          onClick={() => select(c)}
          aria-label={label(c)}
          title={label(c)}
          sx={{
            ...tileSx,
            justifyContent: compact ? 'center' : 'flex-start',
            outline: c === context ? '2px solid white' : 'none',
            outlineOffset: -4,
          }}
        >
          {getFreakIcon(c)}
          {!compact && <Typography sx={{ fontWeight: 700 }}>{label(c)}</Typography>}
        </ButtonBase>
      ))}
      {currentUser && (
        <ButtonBase
          onClick={startEditing}
          aria-label={translation.contextPicker.customize}
          sx={{
            ...tileSx,
            justifyContent: 'center',
            backgroundColor: 'transparent',
            border: `1px dashed ${DEFAULT_THEME_COLOR}`,
            color: DEFAULT_THEME_COLOR,
          }}
        >
          <TuneIcon />
          <Typography sx={{ fontWeight: 700 }}>{translation.contextPicker.customize}</Typography>
        </ButtonBase>
      )}
    </Box>
  );
};

const mapStateToProps = state => ({
  currentUser: state.common.currentUser,
  context: state.common.context || state.common.currentUser?.context,
});

const mapDispatchToProps = dispatch => ({
  onContextChange: payload => dispatch({ type: CONTEXT_CHANGE, payload }),
  onVisibleContextsChange: payload => dispatch({ type: VISIBLE_CONTEXTS_CHANGE, payload }),
});

export default connect(mapStateToProps, mapDispatchToProps)(ContextPicker);
